import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Shield, CreditCard, RefreshCw, AlertTriangle, Lock, FileText, Scale } from "lucide-react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title="Terms of Service" />
      
      <main className="px-6 py-8 max-w-3xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-sage-darker mb-2">Terms of Service</h1>
          <p className="text-muted-foreground">Last updated: December 2024</p>
        </div>

        {/* Payment Terms */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Payment Terms</h2>
            </div>
            <div className="space-y-3 text-sage-darker text-sm">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <p className="font-semibold text-amber-700">All payments are non-refundable</p>
                </div>
              </div>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>All event registrations and expert session bookings require upfront payment</li>
                <li>Payments are processed securely through Razorpay</li>
                <li>Once a payment is completed, it cannot be refunded under any circumstances</li>
                <li>Please ensure you can attend before making a purchase</li>
                <li>Pricing is displayed in Indian Rupees (INR)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Cancellation & Rescheduling */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Cancellation & Rescheduling</h2>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-semibold text-sage-darker mb-2">Event Registrations</h3>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>You may cancel your event registration at any time through your profile</li>
                  <li>Cancellation does not entitle you to a refund</li>
                  <li>Your spot will be released for other attendees</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-sage-darker mb-2">Expert Sessions</h3>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Sessions can be rescheduled up to 24 hours before the scheduled time</li>
                  <li>To reschedule, please contact support at support@wombs.com</li>
                  <li>Rescheduling is subject to expert availability</li>
                  <li>No-shows will not be rescheduled or refunded</li>
                  <li>Sessions cannot be cancelled for a refund</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Conduct */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                <Scale className="w-5 h-5 text-sage-dark" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">User Conduct</h2>
            </div>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground text-sm">
              <li>Treat all community members with respect and kindness</li>
              <li>Do not share personal contact information publicly</li>
              <li>Do not engage in harassment, bullying, or discrimination</li>
              <li>Do not share false or misleading medical advice</li>
              <li>Anonymous posts in circles must still follow community guidelines</li>
              <li>Violation of these terms may result in account suspension</li>
            </ul>
          </CardContent>
        </Card>

        {/* Account Terms */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Account Terms</h2>
            </div>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground text-sm">
              <li>You must provide accurate information when creating your account</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must be at least 18 years old to use this service</li>
              <li>We reserve the right to suspend or terminate accounts that violate our terms</li>
              <li>You may delete your account at any time by contacting support</li>
            </ul>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <Card className="bg-white border-sage/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-serif text-sage-darker">Medical Disclaimer</h2>
            </div>
            <p className="text-muted-foreground text-sm">
              Wombs is a community platform and does not provide medical advice. The information and support provided through our platform, including expert sessions, is for general wellness purposes only and should not be considered a substitute for professional medical advice, diagnosis, or treatment. Always consult with qualified healthcare providers for medical concerns.
            </p>
          </CardContent>
        </Card>

        {/* Contact */}
        <div className="text-center pt-6 border-t border-sage/20">
          <p className="text-muted-foreground text-sm">
            Questions about these terms? Contact us at{" "}
            <a href="mailto:support@wombs.com" className="text-sage-dark underline">support@wombs.com</a>
          </p>
        </div>
      </main>

      <Navigation />
    </div>
  );
};

export default Terms;
